import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { identifyUser, setGroup, resetUser } from '../posthog/index.js';
import {
  onUnauthorized,
  getGatewayUrl,
  configureTokenProvider,
  configureRefreshHandler,
} from '../api/client.js';
import { useImpersonationSession } from './use-impersonation-session.js';
import type { ImpersonationSessionSummary } from '../api/impersonation.js';

interface User {
  userId: string;
  email: string;
  role: string;
  emailVerified?: boolean;
  needsPassword?: boolean;
}

interface RefreshResponseData {
  token: string;
  user: User;
}

interface RefreshApiResponse {
  success: boolean;
  data?: RefreshResponseData;
}

interface AuthState {
  user: User | null;
  token: string | null;
  storeId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  impersonation: ImpersonationSessionSummary | null;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  setStoreId: (storeId: string, storeName?: string) => void;
  updateUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const STORE_KEY = 'halo_store_id';
const STORE_NAME_KEY = 'halo_store_name';

/** Backend returns role as a number but frontend compares as string — normalize. */
function normalizeUser(raw: User): User {
  return { ...raw, role: String(raw.role) };
}

/**
 * Remove every localStorage entry scoped to the Halo app (prefixes `halo_` and
 * `halo:`) while preserving device-level preferences like `theme`. Covers
 * store selection, per-store dashboard prefs, last-access timestamps, view
 * mode, AskHalo sidebar state, and admin ops history.
 */
function clearAppLocalStorage(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('halo_') || key.startsWith('halo:'))) {
      localStorage.removeItem(key);
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [storeId, setStoreIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Compute isAuthenticated early so we can pass it to the impersonation hook
  const isAuthenticated = !!token && !!user;
  const { session: impersonation } = useImpersonationSession(isAuthenticated);

  // Ref to keep logout stable for the auto-refresh timer without adding
  // it to the dependency array (which would cause infinite re-registration).
  const logoutRef = useRef<() => Promise<void>>();

  // ── Helper: update token in both React state and API client synchronously ─
  const updateToken = useCallback((newToken: string | null) => {
    setToken(newToken);
    // Synchronously update the API client so the next request has the token
    // immediately — useEffect would be too late (fires after render).
    configureTokenProvider(() => newToken);
  }, []);

  // ── Refresh — hit /auth/refresh, update state, return new access token ───
  // Used by the API client to rescue requests that 401 with a stale access
  // token (common during deploys). Returns null on failure so the caller can
  // decide whether to log out.
  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(`${getGatewayUrl()}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as RefreshApiResponse;
      if (!data.success || !data.data) return null;
      const normalized = normalizeUser(data.data.user);
      updateToken(data.data.token);
      setUser(normalized);
      return data.data.token;
    } catch {
      return null;
    }
  }, [updateToken]);

  // Register the refresh handler so the API client can rescue 401s.
  useEffect(() => {
    configureRefreshHandler(refresh);
  }, [refresh]);

  // ── Silent refresh on mount ──────────────────────────────────────────────
  useEffect(() => {
    async function initAuth() {
      try {
        const res = await fetch(`${getGatewayUrl()}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = (await res.json()) as RefreshApiResponse;
          if (data.success && data.data) {
            const normalized = normalizeUser(data.data.user);
            updateToken(data.data.token);
            setUser(normalized);
            Sentry.setUser({ id: normalized.userId, email: normalized.email });
            identifyUser(normalized.userId, { email: normalized.email });
          }
        }
      } catch {
        // No valid session — user must log in
      }

      // Restore store context from localStorage (always needed)
      const savedStore = localStorage.getItem(STORE_KEY);
      if (savedStore) {
        setStoreIdState(savedStore);
      }

      setIsLoading(false);
    }
    initAuth();
  }, [updateToken]);

  // ── Login — store token in memory only ───────────────────────────────────
  const login = useCallback((newToken: string, newUser: User) => {
    const normalized = normalizeUser(newUser);
    updateToken(newToken);
    setUser(normalized);
    Sentry.setUser({ id: normalized.userId, email: normalized.email });
    identifyUser(normalized.userId, { email: normalized.email });
  }, [updateToken]);

  // ── Logout — revoke refresh cookie, clear state ─────────────────────────
  const logout = useCallback(async () => {
    try {
      await fetch(`${getGatewayUrl()}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best effort — clear local state regardless
    }
    updateToken(null);
    setUser(null);
    setStoreIdState(null);
    clearAppLocalStorage();
    queryClient.clear();
    Sentry.setUser(null);
    resetUser();
  }, [updateToken, queryClient]);

  // Keep the ref current so the timer callback always calls the latest logout
  logoutRef.current = logout;

  // ── Register with api client so 401 responses clear auth state ───────────
  useEffect(() => {
    onUnauthorized(() => {
      void logoutRef.current?.();
    });
  }, []);

  // ── Auto-refresh timer — refresh 1 minute before access token expires ────
  useEffect(() => {
    if (!token) return;

    // Decode JWT payload (base64url → base64 → JSON)
    const parts = token.split('.');
    if (parts.length !== 3) return;

    const payloadPart = parts[1];
    if (!payloadPart) return;
    const base64 = payloadPart
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const payload: { exp?: number } = JSON.parse(atob(base64));
    if (!payload.exp) return;

    const expiresAt = payload.exp * 1000;
    const refreshAt = expiresAt - 60_000; // 1 minute before expiry
    const timeout = Math.max(refreshAt - Date.now(), 0);

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${getGatewayUrl()}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = (await res.json()) as RefreshApiResponse;
          if (data.success && data.data) {
            updateToken(data.data.token);
            if (data.data.user) {
              setUser(normalizeUser(data.data.user));
            }
          } else {
            void logoutRef.current?.();
          }
        } else {
          void logoutRef.current?.();
        }
      } catch {
        void logoutRef.current?.();
      }
    }, timeout);

    return () => clearTimeout(timer);
  }, [token, updateToken]);

  const setStoreId = useCallback((id: string, name?: string) => {
    // Capture the previous storeId BEFORE overwriting so we can detect
    // a switch and invalidate a stale name label.
    const previousId = localStorage.getItem(STORE_KEY);
    setStoreIdState(id);
    localStorage.setItem(STORE_KEY, id);
    if (name) {
      localStorage.setItem(STORE_NAME_KEY, name);
    } else if (previousId !== null && previousId !== id) {
      // Switching to a different store without a name — clear the
      // stale label so the sidebar falls back cleanly instead of
      // showing the previous store's name.
      localStorage.removeItem(STORE_NAME_KEY);
    }
    setGroup('store', id);
  }, []);

  const updateUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...patch } : prev);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        storeId: impersonation ? String(impersonation.storeId) : storeId,
        isAuthenticated,
        isLoading,
        impersonation,
        login,
        logout,
        setStoreId,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
